import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { Alignment, Side } from 'driver.js';
import { TOUR_ANCHORS, type TourAnchor } from './anchors';

export type TourId = 'manage-hub' | 'explore-hub';

export interface TourStep {
    anchor: TourAnchor;
    title: MessageDescriptor;
    body: MessageDescriptor;
    mode: 'look' | 'try';
    side?: Side;
    align?: Alignment;
}

const manage = defineMessages({
    heroTitle: {
        id: 'e87624e7-3196-4936-8525-b75ab98e8431',
        defaultMessage: 'Your home is ready',
    },
    heroBody: {
        id: 'add4f885-1d3a-484f-a805-0489d5925928',
        defaultMessage:
            'This is the Home Hub for {homeName}. Find important updates and your group info here.',
    },
    captureTitle: {
        id: 'f45a9214-cc25-430f-8fd3-e29fce9a09a7',
        defaultMessage: 'Capture in one place',
    },
    captureBody: {
        id: '98010778-9d60-4176-a435-4be4f08b2b9d',
        defaultMessage:
            'Drop a receipt or booking, or add an expense or calendar event. CoHaven auto-files what it can.',
    },
    inboxTitle: {
        id: '086b853a-1bc1-4431-9902-ab7ec6c5d295',
        defaultMessage: 'Your home inbox',
    },
    inboxBody: {
        id: '233ed6fc-88da-468f-8c29-4b464c8ceb28',
        defaultMessage:
            'Forward mail to this address. Same idea as dropping a file — unique to this home.',
    },
    activityTitle: {
        id: '49e008d9-34d4-472c-8f44-e8b942db3358',
        defaultMessage: 'Around the home',
    },
    activityBody: {
        id: 'db67f429-6869-40c7-8acc-f0c0cbffa8a1',
        defaultMessage:
            'A running record of what happened. Activity shows up here to keep you in the loop.',
    },
    thisWeekTitle: {
        id: 'e0a27160-c6fb-4ee9-b820-949b927b7698',
        defaultMessage: 'This week',
    },
    thisWeekBody: {
        id: '2319522f-0902-47a1-97da-aaf6a617c319',
        defaultMessage: 'Stays, visits, and upcoming dates. View the calendar for a closer look.',
    },
    financesTitle: {
        id: 'cb8d6843-1209-4bb8-a484-f86549817bb5',
        defaultMessage: 'Shared money',
    },
    financesBody: {
        id: '374924e8-7ead-4989-89f7-5271caa2ab36',
        defaultMessage:
            'Month-to-date spend and anything overdue. Links to Finances for more info.',
    },
    navTitle: {
        id: '5befa7df-cffa-4e62-9e33-6931a10850e9',
        defaultMessage: 'The rest of the house in a tap',
    },
    navBody: {
        id: '9ab18e69-e51a-4392-ad87-bf1adc749398',
        defaultMessage:
            'See Activity, Finances, Calendar, Documents, and Settings from here. Now you’re ready to get started!',
    },
});

const explore = defineMessages({
    heroTitle: {
        id: '9115715a-278e-4807-8ccc-7e691d54be6f',
        defaultMessage: 'Your group is ready',
    },
    heroBody: {
        id: 'e2e9bac2-2110-465f-9da2-e0e50ada0296',
        defaultMessage:
            'This is Explore — searching for a home with {groupName}, not running one yet. Switch groups by clicking the drop down, and see your group’s members and goal.',
    },
    milestonesTitle: {
        id: '92e9f0c3-7117-4c77-9e63-0f88536f0e0c',
        defaultMessage: 'Path to co-ownership',
    },
    milestonesBody: {
        id: 'd42cd906-04b3-499f-aacd-96cb9d904319',
        defaultMessage:
            'You’re on your way to finding a CoHaven - you can see more on the steps involved and your progress here.',
    },
    captureTitle: {
        id: 'd06b8fb4-8fe2-4a63-9571-d9b97c408fd8',
        defaultMessage: 'Add a home',
    },
    captureBody: {
        id: 'cc71fc47-0234-40db-9330-f9b51ed9a16c',
        defaultMessage:
            'Paste a listing link or drop a screenshot. It lands in the group’s saved listings so the thread does not live in a chat.',
    },
    inboxTitle: {
        id: '6158baee-ec1b-4837-9e07-cb508c883fa6',
        defaultMessage: 'Your group inbox',
    },
    inboxBody: {
        id: 'ee6b2f3f-64fa-41f6-b6b5-2cdc999862cc',
        defaultMessage:
            'Your dedicated group inbox is ready. Forward a listing to this address or use our chrome extension for an even easier way to keep your group in the loop.',
    },
    waitingTitle: {
        id: '6a35b60d-733f-40a2-a33d-317851270463',
        defaultMessage: 'Waiting on you',
    },
    waitingBody: {
        id: '82eb8dfd-10fd-4fb5-abcc-6239e4af31d8',
        defaultMessage:
            'Homes nobody has heard from you on. Your vote is how a pile of links becomes a shortlist.',
    },
    activityTitle: {
        id: '9a2b3327-f3b8-497a-8d9e-2165fab31b10',
        defaultMessage: 'Group activity',
    },
    activityBody: {
        id: '64c2940f-37ee-4eae-bcae-750465472ce2',
        defaultMessage: 'What the group just did - new listings, votes, comments.',
    },
    navTitle: {
        id: 'ca6de665-e98b-4af0-8374-70ff67e42af7',
        defaultMessage: 'The rest of Explore',
    },
    navBody: {
        id: '76d900b1-0ea3-4e9a-9869-4810f89a7224',
        defaultMessage:
            'Activity, Saved listings, and Preferences. Once you find your dream property, switch over to manage to get instant access via the drop down.',
    },
});

export const TOURS: Record<TourId, TourStep[]> = {
    'manage-hub': [
        {
            anchor: TOUR_ANCHORS.hubHero,
            title: manage.heroTitle,
            body: manage.heroBody,
            mode: 'look',
            side: 'bottom',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.hubQuickCapture,
            title: manage.captureTitle,
            body: manage.captureBody,
            mode: 'look',
            side: 'right',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.hubInbox,
            title: manage.inboxTitle,
            body: manage.inboxBody,
            mode: 'try',
            side: 'bottom',
            align: 'end',
        },
        {
            anchor: TOUR_ANCHORS.hubActivityDigest,
            title: manage.activityTitle,
            body: manage.activityBody,
            mode: 'look',
            side: 'right',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.hubThisWeek,
            title: manage.thisWeekTitle,
            body: manage.thisWeekBody,
            mode: 'look',
            side: 'left',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.hubFinances,
            title: manage.financesTitle,
            body: manage.financesBody,
            mode: 'look',
            side: 'left',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.sidebarNav,
            title: manage.navTitle,
            body: manage.navBody,
            mode: 'look',
            side: 'right',
            align: 'center',
        },
    ],

    'explore-hub': [
        {
            anchor: TOUR_ANCHORS.groupHubHero,
            title: explore.heroTitle,
            body: explore.heroBody,
            mode: 'look',
            side: 'bottom',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.groupMilestones,
            title: explore.milestonesTitle,
            body: explore.milestonesBody,
            mode: 'look',
            side: 'bottom',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.groupCapture,
            title: explore.captureTitle,
            body: explore.captureBody,
            mode: 'look',
            side: 'bottom',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.groupInbox,
            title: explore.inboxTitle,
            body: explore.inboxBody,
            mode: 'try',
            side: 'bottom',
            align: 'end',
        },
        {
            anchor: TOUR_ANCHORS.groupWaitingOnYou,
            title: explore.waitingTitle,
            body: explore.waitingBody,
            mode: 'look',
            side: 'left',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.groupActivityDigest,
            title: explore.activityTitle,
            body: explore.activityBody,
            mode: 'look',
            side: 'right',
            align: 'start',
        },
        {
            anchor: TOUR_ANCHORS.sidebarNav,
            title: explore.navTitle,
            body: explore.navBody,
            mode: 'look',
            side: 'right',
            align: 'center',
        },
    ],
};
